import { z } from 'zod';

/**
 * Cron schedules: the standard five fields (minute hour day-of-month month day-of-week) with `*`,
 * lists, ranges, steps and month/day names, plus `@hourly`, `@daily`, `@weekly`, `@monthly` and
 * `@yearly`. As in Vixie cron, when both day fields are restricted a day matches if either does.
 * Times are evaluated in an IANA time zone, so schedules follow daylight saving time.
 */

const MACROS: Record<string, string> = {
  '@yearly': '0 0 1 1 *',
  '@annually': '0 0 1 1 *',
  '@monthly': '0 0 1 * *',
  '@weekly': '0 0 * * 0',
  '@daily': '0 0 * * *',
  '@midnight': '0 0 * * *',
  '@hourly': '0 * * * *',
};
const MONTHS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
const DAYS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

interface Field {
  min: number;
  max: number;
  names?: string[];
  /** Names map to `index + offset` (months start at 1). */
  offset?: number;
}
const FIELDS: Field[] = [
  { min: 0, max: 59 },
  { min: 0, max: 23 },
  { min: 1, max: 31 },
  { min: 1, max: 12, names: MONTHS, offset: 1 },
  { min: 0, max: 7, names: DAYS, offset: 0 },
];
const FIELD_NAMES = ['minute', 'hour', 'day of month', 'month', 'day of week'];

export interface ParsedCron {
  minutes: Set<number>;
  hours: Set<number>;
  daysOfMonth: Set<number>;
  months: Set<number>;
  daysOfWeek: Set<number>;
  /** Whether each day field was restricted (not `*`). */
  domRestricted: boolean;
  dowRestricted: boolean;
}

export class CronError extends Error {
  override readonly name = 'CronError';
}

function value(token: string, field: Field, name: string): number {
  const upper = token.toUpperCase();
  const named = field.names?.indexOf(upper) ?? -1;
  const n = named >= 0 ? named + (field.offset ?? 0) : /^\d+$/.test(token) ? Number(token) : NaN;
  if (!Number.isInteger(n) || n < field.min || n > field.max)
    throw new CronError(`Invalid ${name} “${token}” (use ${field.min}–${field.max})`);
  return n;
}

function parseField(
  text: string,
  field: Field,
  name: string,
): { values: Set<number>; any: boolean } {
  const values = new Set<number>();
  let any = false;
  for (const part of text.split(',')) {
    const [range, stepText] = part.split('/');
    if (range === undefined || range === '') throw new CronError(`Invalid ${name} “${text}”`);
    const step = stepText === undefined ? 1 : Number(stepText);
    if (!Number.isInteger(step) || step < 1)
      throw new CronError(`Invalid step in ${name} “${part}”`);
    let from: number;
    let to: number;
    if (range === '*') {
      from = field.min;
      to = field.max;
      if (stepText === undefined) any = true;
    } else if (range.includes('-')) {
      const [a, b] = range.split('-');
      from = value(a ?? '', field, name);
      to = value(b ?? '', field, name);
      if (from > to) throw new CronError(`Invalid range in ${name} “${range}”`);
    } else {
      from = value(range, field, name);
      to = stepText === undefined ? from : field.max;
    }
    for (let v = from; v <= to; v += step) values.add(v);
  }
  return { values, any };
}

/** Parses a cron expression; throws a `CronError` explaining what is wrong. */
export function parseCron(expression: string): ParsedCron {
  const text = MACROS[expression.trim().toLowerCase()] ?? expression.trim();
  const parts = text.split(/\s+/);
  if (parts.length !== 5)
    throw new CronError('Use five fields: minute hour day-of-month month day-of-week');
  const [m, h, dom, mon, dow] = parts.map((p, i) => parseField(p, FIELDS[i]!, FIELD_NAMES[i]!));
  const daysOfWeek = new Set([...dow!.values].map((d) => (d === 7 ? 0 : d)));
  return {
    minutes: m!.values,
    hours: h!.values,
    daysOfMonth: dom!.values,
    months: mon!.values,
    daysOfWeek,
    domRestricted: !dom!.any,
    dowRestricted: !dow!.any,
  };
}

const formatters = new Map<string, Intl.DateTimeFormat>();
function partsIn(date: Date, timeZone: string) {
  let f = formatters.get(timeZone);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      weekday: 'short',
    });
    formatters.set(timeZone, f);
  }
  const out: Record<string, string> = {};
  for (const p of f.formatToParts(date)) out[p.type] = p.value;
  return {
    month: Number(out.month),
    day: Number(out.day),
    hour: Number(out.hour),
    minute: Number(out.minute),
    weekday: DAYS.indexOf(out.weekday!.toUpperCase().slice(0, 3)),
  };
}

export function isValidTimeZone(timeZone: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone });
    return true;
  } catch {
    return false;
  }
}

const dayMatches = (c: ParsedCron, day: number, weekday: number) => {
  const dom = c.daysOfMonth.has(day);
  const dow = c.daysOfWeek.has(weekday);
  if (c.domRestricted && c.dowRestricted) return dom || dow;
  return dom && dow;
};

/** The first fire time strictly after `after` (minute precision), or null within ~5 years. */
export function nextCronTime(
  cron: ParsedCron | string,
  after: Date,
  timeZone = 'UTC',
): Date | null {
  const c = typeof cron === 'string' ? parseCron(cron) : cron;
  // Start at the next whole minute.
  let t = Math.floor(after.getTime() / 60_000) * 60_000 + 60_000;
  const limit = t + 5 * 366 * 86_400_000;
  while (t < limit) {
    const p = partsIn(new Date(t), timeZone);
    if (!c.months.has(p.month) || !dayMatches(c, p.day, p.weekday)) {
      // Jump to the next local midnight.
      t += ((23 - p.hour) * 60 + (60 - p.minute)) * 60_000;
      continue;
    }
    if (!c.hours.has(p.hour)) {
      t += (60 - p.minute) * 60_000;
      continue;
    }
    if (!c.minutes.has(p.minute)) {
      t += 60_000;
      continue;
    }
    return new Date(t);
  }
  return null;
}

/** The next `count` fire times after `after`. */
export function nextCronTimes(cron: string, after: Date, timeZone = 'UTC', count = 3): Date[] {
  const parsed = parseCron(cron);
  const out: Date[] = [];
  let from = after;
  for (let i = 0; i < count; i++) {
    const next = nextCronTime(parsed, from, timeZone);
    if (!next) break;
    out.push(next);
    from = next;
  }
  return out;
}

export const cronExpressionSchema = z
  .string()
  .trim()
  .min(1, 'Enter a cron expression')
  .max(200)
  .superRefine((expr, ctx) => {
    try {
      const parsed = parseCron(expr);
      if (!nextCronTime(parsed, new Date(), 'UTC'))
        ctx.addIssue({ code: 'custom', message: 'This schedule never runs' });
    } catch (error) {
      ctx.addIssue({ code: 'custom', message: (error as Error).message });
    }
  });

export const timeZoneSchema = z
  .string()
  .trim()
  .min(1)
  .max(100)
  .refine(isValidTimeZone, { message: 'Unknown time zone' });

/** Plain-language labels for common expressions (the console shows the raw cron otherwise). */
export const CRON_PRESETS: Array<{ label: string; cron: string }> = [
  { label: 'Every 15 minutes', cron: '*/15 * * * *' },
  { label: 'Every hour', cron: '0 * * * *' },
  { label: 'Every day at 9:00', cron: '0 9 * * *' },
  { label: 'Weekdays at 9:00', cron: '0 9 * * 1-5' },
  { label: 'Every Monday at 8:00', cron: '0 8 * * 1' },
  { label: 'First day of the month at 0:00', cron: '0 0 1 * *' },
];
